// RELY - Base Layout (Corporate Design)
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import Navbar from 'components/navbar';
import Sidebar from 'components/sidebar';
import Footer from 'components/footer/Footer';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { getMobileRouteChrome } from 'components/navigation/mobileRouteChrome';
import routes from 'routes.js';
import { useNavigation } from 'hooks/useNavigation';
import { cn } from 'lib/utils';
import { isLocalAuthBypassEnabled } from 'config/localAuthBypass';
import EnterpriseDiscovery from 'views/enterprise/EnterpriseDiscovery';

/**
 * Layout Base Unificado - Corporate Design
 * @param {Object} props - Props del layout
 * @param {string} props.layoutType - Tipo de layout: 'admin' o 'empresa'
 * @param {string} props.defaultRoute - Ruta por defecto para navegación
 */
export default function BaseLayout({
  layoutType = 'admin',
  defaultRoute = '/panel',
  ...rest
}) {
  const [open, setOpen] = React.useState(true);
  const [collapsed, setCollapsed] = React.useState(false);
  const { getRoutes } = useNavigation(routes);
  const { isAuthenticated, isLoading } = useAuth0();
  const { t } = useTranslation();
  const location = useLocation();
  React.useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  // Responsive sidebar handler
  React.useEffect(() => {
    const handleResize = () => {
      setOpen(window.innerWidth >= 1200);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate routes based on layout type
  const routeElements = getRoutes(routes, layoutType).map((route) => (
    <Route
      key={route.key}
      path={route.path}
      element={
        <ErrorBoundary resetKey={location.pathname}>
          {route.element}
        </ErrorBoundary>
      }
    />
  ));

  // Set document direction
  document.documentElement.dir = 'ltr';

  // Determinar si mostrar sidebar y navbar
  const showSidebarAndNavbar =
    isLocalAuthBypassEnabled() || (isAuthenticated && !isLoading);
  const mobileChrome = getMobileRouteChrome(location.pathname, { layoutType });
  const reserveMobileDockClearance =
    showSidebarAndNavbar && mobileChrome.reserveDockClearance;

  return (
    <div className="flex h-full w-full bg-[var(--rely-surface-page)] text-[var(--rely-text-strong)]">
      <a
        href="#main-content"
        className={cn(
          'sr-only focus:not-sr-only',
          'fixed left-4 top-4 z-[9999] rounded-lg px-4 py-2',
          'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] text-sm font-semibold text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-panel)]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rely-surface-page)]'
        )}
      >
        {t('accessibility.skipToMainContent', 'Skip to main content')}
      </a>
      {/* Sidebar - Desktop only (xl+) */}
      {showSidebarAndNavbar && (
        <div className="hidden xl:block">
          <Sidebar
            open={open}
            collapsed={collapsed}
            onClose={() => setOpen(false)}
            onToggleCollapse={() => setCollapsed(!collapsed)}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="min-h-screen flex-1 overflow-x-hidden">
        {/* Navbar - Fixed at top */}
        {showSidebarAndNavbar && <Navbar layoutType={layoutType} />}

        {/* Page Content */}
        <main
          id="main-content"
          tabIndex={-1}
          data-mobile-safe-area={
            reserveMobileDockClearance ? 'dock' : undefined
          }
          className={cn(
            'flex min-h-screen flex-col',
            'bg-[var(--rely-surface-page)]',
            // Padding for navbar on mobile/tablet
            showSidebarAndNavbar && 'pt-16 md:pt-16',
            // Keep mobile app navigation from covering page content
            reserveMobileDockClearance &&
              'scroll-pb-[var(--rely-mobile-dock-clearance)]',
            showSidebarAndNavbar && !reserveMobileDockClearance && 'md:pb-0',
            // Sidebar margin on desktop
            showSidebarAndNavbar && !collapsed && 'xl:ml-[304px]',
            showSidebarAndNavbar && collapsed && 'xl:ml-[88px]',
            // Remove top padding on desktop (sidebar layout)
            showSidebarAndNavbar && 'xl:pt-0'
          )}
        >
          <div
            data-rely-route-content="true"
            className="mx-auto min-h-[calc(100dvh-4rem)] w-full max-w-7xl flex-1 px-4 pb-6 pt-6 sm:px-6 lg:px-8"
          >
            {layoutType === 'empresa' && showSidebarAndNavbar && (
              <EnterpriseDiscovery />
            )}
            <Routes>
              {routeElements}
              <Route
                path={`/${layoutType}`}
                element={<Navigate to={defaultRoute} replace />}
              />
            </Routes>
          </div>

          <div
            id="rely-cookie-banner-slot"
            className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
          />

          {/* Footer */}
          {showSidebarAndNavbar && (
            <div
              data-rely-app-footer-slot="true"
              className="px-4 pb-6 sm:px-6 lg:px-8"
            >
              <Footer reserveMobileDockClearance={reserveMobileDockClearance} />
            </div>
          )}
          {showSidebarAndNavbar && reserveMobileDockClearance && (
            <div
              aria-hidden="true"
              data-rely-mobile-dock-spacer="true"
              className="h-[var(--rely-mobile-dock-clearance)] md:hidden"
            />
          )}
        </main>
      </div>
    </div>
  );
}
